/**
 * useGedcomImport Hook
 *
 * Orchestrates the complete GEDCOM import flow:
 * Upload → Parse → Validate → Transform → Import
 *
 * Part of Phase 4 - Slice 9: Frontend Import Flow Integration
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/config/supabase';
import type { ParseResult } from './useGedcomParse';
import type { GedcomImportPlan } from '../types/GedcomImportPlan';
import type { ValidationResult } from '../types/GedcomValidation';

/**
 * Import phases for progress tracking
 */
export type ImportPhase =
  | 'idle'
  | 'uploading'
  | 'parsing'
  | 'validating'
  | 'transforming'
  | 'importing'
  | 'complete'
  | 'error';

/**
 * Import statistics returned from RPC
 */
export interface ImportStatistics {
  persons_imported: number;
  couples_imported: number;
  links_created: number;
  facts_imported: number;
  spouse_relationships_created?: number;
  parent_child_relationships_created?: number;
  relationships_created?: number;
}

/**
 * ID mappings from temp IDs to real UUIDs
 */
export interface IdMappings {
  persons: Record<string, string>;
  couples: Record<string, string>;
}

/**
 * Complete import result
 */
export interface ImportResult {
  success: boolean;
  tree_id?: string;
  statistics?: ImportStatistics;
  id_mappings?: IdMappings;
  error?: string;
  error_detail?: string;
  error_context?: string;
}

/**
 * Hook configuration options
 */
export interface UseGedcomImportOptions {
  /** Tree ID to import into (null to create new tree) */
  treeId?: string | null;

  /** Tree name (required if creating new tree) */
  treeName?: string;

  /** Callback when phase changes */
  onPhaseChange?: (phase: ImportPhase, percent: number) => void;

  /** Callback when import completes */
  onComplete?: (result: ImportResult) => void;

  /** Callback when import fails */
  onError?: (error: string) => void;
}

/**
 * Hook return interface
 */
export interface UseGedcomImportReturn {
  /** Current import phase */
  phase: ImportPhase;

  /** Progress percentage (0-100) */
  progress: number;

  /** Parse result from GEDCOM parsing */
  parseResult: ParseResult | null;

  /** Validation result */
  validation: ValidationResult | null;

  /** Import plan built from parsed data */
  importPlan: GedcomImportPlan | null;

  /** Final import result */
  importResult: ImportResult | null;

  /** Error message if import fails */
  error: string | null;

  /** Execute the full import flow */
  executeImport: (
    file: File,
    parseResult: ParseResult,
    importPlan: GedcomImportPlan
  ) => Promise<ImportResult>;

  /** Reset hook state */
  reset: () => void;

  /** Check if import can proceed */
  canImport: boolean;
}

/**
 * Custom hook for orchestrating GEDCOM import
 */
export const useGedcomImport = (options: UseGedcomImportOptions = {}): UseGedcomImportReturn => {
  const { treeId, treeName, onPhaseChange, onComplete, onError } = options;

  // State
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [progress, setProgress] = useState(0);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [importPlan, setImportPlan] = useState<GedcomImportPlan | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Update phase and progress
   */
  const updatePhase = useCallback((newPhase: ImportPhase, percent: number) => {
    setPhase(newPhase);
    setProgress(percent);
    onPhaseChange?.(newPhase, percent);
  }, [onPhaseChange]);

  /**
   * Execute the complete import flow
   */
  const executeImport = useCallback(async (
    file: File,
    parsedData: ParseResult,
    plan: GedcomImportPlan
  ): Promise<ImportResult> => {
    try {
      // Reset state
      setError(null);
      setImportResult(null);

      // Store parse result and import plan
      setParseResult(parsedData);
      setValidation(parsedData.validation);
      setImportPlan(plan);

      // Phase 1: Already uploaded (10%)
      updatePhase('uploading', 10);

      // Phase 2: Already parsed (30%)
      updatePhase('parsing', 30);

      // Phase 3: Already validated (50%)
      updatePhase('validating', 50);

      // Phase 4: Already transformed (70%)
      updatePhase('transforming', 70);

      // Phase 5: Import to database (90%)
      updatePhase('importing', 90);

      // Check if import is blocked
      if (!plan.summary.ready_for_import) {
        const errorMsg = 'Import blocked due to validation errors';
        setError(errorMsg);
        updatePhase('error', 90);
        onError?.(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }

      // Call the import_gedcom_data RPC function
      console.log('[GEDCOM Import] Calling RPC with plan:', {
        treeId,
        treeName,
        personCount: plan.persons.length,
        coupleCount: plan.couples.length,
      });

      const { data, error: rpcError } = await supabase.rpc('import_gedcom_data', {
        p_tree_id: treeId || null,
        p_tree_name: treeName || null,
        p_import_plan: plan as any, // Cast to any for JSONB parameter
      });

      if (rpcError) {
        console.error('[GEDCOM Import] RPC error:', rpcError);
        const errorMsg = rpcError.message || 'Failed to import GEDCOM data';
        setError(errorMsg);
        updatePhase('error', 90);
        onError?.(errorMsg);
        return {
          success: false,
          error: errorMsg,
          error_detail: rpcError.details || rpcError.hint || '',
        };
      }

      // Check RPC response
      if (!data || !data.success) {
        const errorMsg = data?.error || 'Import failed';
        const errorDetail = data?.error_detail || '';
        const errorContext = data?.error_context || '';

        console.error('[GEDCOM Import] Import failed:', {
          error: errorMsg,
          detail: errorDetail,
          context: errorContext,
        });

        setError(errorMsg);
        updatePhase('error', 90);
        onError?.(errorMsg);

        return {
          success: false,
          error: errorMsg,
          error_detail: errorDetail,
          error_context: errorContext,
        };
      }

      // Success!
      const result: ImportResult = {
        success: true,
        tree_id: data.tree_id,
        statistics: data.statistics,
        id_mappings: data.id_mappings,
      };

      console.log('[GEDCOM Import] Import successful:', result.statistics);

      setImportResult(result);
      updatePhase('complete', 100);
      onComplete?.(result);

      return result;

    } catch (err) {
      console.error('[GEDCOM Import] Unexpected error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Unexpected error during import';
      setError(errorMsg);
      updatePhase('error', 0);
      onError?.(errorMsg);

      return {
        success: false,
        error: errorMsg,
      };
    }
  }, [treeId, treeName, updatePhase, onComplete, onError]);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    setPhase('idle');
    setProgress(0);
    setParseResult(null);
    setValidation(null);
    setImportPlan(null);
    setImportResult(null);
    setError(null);
  }, []);

  /**
   * Check if import can proceed
   */
  const canImport = Boolean(
    importPlan &&
    importPlan.summary.ready_for_import &&
    phase !== 'importing' &&
    phase !== 'complete'
  );

  return {
    phase,
    progress,
    parseResult,
    validation,
    importPlan,
    importResult,
    error,
    executeImport,
    reset,
    canImport,
  };
};